import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchWorkspaces, updateWorkspace, deleteWorkspace, optimisticDeleteWorkspace } from '../../redux/Slices/workspaceSlice';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { find, getCurrentUserWorkspaceRole } from '../../utils/helpers';
import WorkspaceWhiteLabelConfig from './WorkspaceWhiteLabelConfig';
import ApiKeysSection from './ApiKeysSection';
import WebhooksSection from './WebhooksSection';
import Edit from '../icons/Edit';
import Spinner from '../Global/Spinner';
import './scss/WorkspaceSettings.scss';
import './scss/ApiKeysSection.scss';
import './scss/WebhooksSection.scss';

const WorkspaceSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { shortId } = useParams();

  const { workspaces } = useSelector(state => state.workspaces);
  const { user } = useSelector(state => state.user);

  const [isEditingWorkspaceName, setIsEditingWorkspaceName] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceUpdateStatus, setWorkspaceUpdateStatus] = useState('saved');
  const [loading, setLoading] = useState(true);

  // Delete-workspace flow (type-to-confirm)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch workspaces if not loaded
  useEffect(() => {
    if (workspaces.length === 0) {
      dispatch(fetchWorkspaces())
        .unwrap()
        .catch((error) => {
          console.error('Error fetching workspaces:', error);
          dispatch(showSnackBar({
            message: 'Failed to load workspace',
            type: 'error'
          }));
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [dispatch, workspaces.length]);

  // Get current workspace
  const workspace = find.get(workspaces, { name: 'workspace', workspace: shortId }) || null;

  // Initialize workspace name when workspace changes
  useEffect(() => {
    if (workspace) {
      setWorkspaceName(workspace.name);
    }
  }, [workspace]);

  // Handle workspace name update
  const handleUpdateWorkspaceName = async () => {
    if (!workspace) return;

    if (!workspaceName.trim()) {
      dispatch(showSnackBar({
        message: 'Workspace name cannot be empty',
        type: 'error'
      }));
      setWorkspaceName(workspace.name); // Reset to original
      setIsEditingWorkspaceName(false);
      return;
    }

    if (workspaceName.trim() === workspace.name) {
      setIsEditingWorkspaceName(false);
      return;
    }
 
    try {
      setWorkspaceUpdateStatus('saving');

      await dispatch(updateWorkspace({
        workspaceId: workspace.shortId,
        name: workspaceName.trim()
      })).unwrap();

      setWorkspaceUpdateStatus('saved');
      setIsEditingWorkspaceName(false);

      dispatch(showSnackBar({
        message: 'Workspace name updated successfully',
        type: 'success'
      }));

      // Reset status after 2 seconds
      setTimeout(() => {
        setWorkspaceUpdateStatus('saved');
      }, 2000);

    } catch (error) {
      console.error('Error updating workspace name:', error);
      setWorkspaceUpdateStatus('error');
      setWorkspaceName(workspace.name); // Reset to original

      dispatch(showSnackBar({
        message: error.message || 'Failed to update workspace name',
        type: 'error'
      }));

      // Reset status after 3 seconds
      setTimeout(() => {
        setWorkspaceUpdateStatus('saved');
      }, 3000);
    }
  };

  // Handle keyboard events for workspace name input
  const handleWorkspaceNameKeyDown = (e) => {
    if (!workspace) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      handleUpdateWorkspaceName();
    } else if (e.key === 'Escape') {
      setWorkspaceName(workspace.name); // Reset to original
      setIsEditingWorkspaceName(false);
    }
  };

  // Only the workspace owner or an admin may delete it (mirrors the backend check).
  const currentUserId = user?.user?._id;
  const isOwner = workspace?.ownerId && currentUserId &&
    workspace.ownerId.toString() === currentUserId.toString();
  const isAdmin = getCurrentUserWorkspaceRole(workspace, currentUserId) === 'admin';
  const canDeleteWorkspace = Boolean(isOwner || isAdmin);

  // Boards that will be removed by the delete (exclude already-archived ones).
  const boardsToDelete = (workspace?.boards || []).filter(b => !b.archived);
  // Cap the rendered rows so the modal stays compact when a workspace has many
  // boards; the remainder is summarized as "+N more".
  const MAX_PREVIEW_BOARDS = 8;
  const previewBoards = boardsToDelete.slice(0, MAX_PREVIEW_BOARDS);
  const remainingBoardCount = boardsToDelete.length - previewBoards.length;

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setDeleteConfirm('');
  };

  const handleDeleteWorkspace = async () => {
    if (!workspace || deleteConfirm !== workspace.name) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteWorkspace({
        workspaceId: workspace.shortId,
        confirmName: deleteConfirm
      })).unwrap();

      // Remove locally right away; the socket WORKSPACE_DELETED event also fires
      // for other sessions/members.
      dispatch(optimisticDeleteWorkspace(workspace.shortId));
      dispatch(showSnackBar({
        message: 'Workspace deleted successfully',
        type: 'success'
      }));
      setShowDeleteModal(false);
      setDeleteConfirm('');
      navigate('/');
    } catch (error) {
      console.error('Error deleting workspace:', error);
      dispatch(showSnackBar({
        message: error.message || 'Failed to delete workspace',
        type: 'error'
      }));
    } finally {
      setIsDeleting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="workspace-settings-page">
        <div className="workspace-settings-loading">
          <Spinner />
        </div>
      </div>
    );
  }

  // Workspace not found
  if (!workspace) {
    return (
      <div className="workspace-settings-page">
        <div className="workspace-settings-error">
          <h2>Workspace Not Found</h2>
          <p>The workspace you're looking for doesn't exist or you don't have access to it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-settings-page">
      <div className="workspace-settings-container">
        {/* Workspace Name Section */}
        <div className="setting-row">
          <div className="setting-label">
            <h2>Workspace Name</h2>
            <p>Update your workspace name to better reflect your team or project.</p>
          </div>
          <div className="workspace-name-content">
            <div className="workspace-name-edit">
              {isEditingWorkspaceName ? (
                <input
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  onBlur={handleUpdateWorkspaceName}
                  onKeyDown={handleWorkspaceNameKeyDown}
                  className="workspace-name-input"
                  autoFocus
                  maxLength={50}
                />
              ) : (
                <div
                  className="workspace-name-display"
                  onClick={() => setIsEditingWorkspaceName(true)}
                >
                  <span className="workspace-name-text">{workspace.name}</span>
                  <Edit size={16} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Auto-save Status */}
        {/* {workspaceUpdateStatus && workspaceUpdateStatus !== 'saved' && (
          <div className="setting-row">
            <div className="setting-label"></div>
            <div className="auto-save-status">
              {workspaceUpdateStatus === 'saving' && (
                <span className="save-status saving">
                  <span className="material-symbols-outlined">sync</span>
                  Saving...
                </span>
              )}
              {workspaceUpdateStatus === 'error' && (
                <span className="save-status error">
                  <span className="material-symbols-outlined">error</span>
                  Save failed
                </span>
              )}
            </div>
          </div>
        )} */}

        {/* White Label Configuration Section */}
        <WorkspaceWhiteLabelConfig
          workspace={workspace}
          currentUser={user}
        />

        {/* API Keys Section */}
        <ApiKeysSection workspace={workspace} />

        {/* Webhooks Section */}
        <WebhooksSection workspace={workspace} />

        {/* Danger Zone — owner/admin only */}
        {canDeleteWorkspace && (
          <div className="setting-row danger-zone">
            <div className="setting-label">
              <h2>Delete Workspace</h2>
              <p>
                Permanently delete this workspace and all of its boards, lists,
                and cards. This action cannot be undone.
              </p>
            </div>
            <div className="danger-zone-content">
              <button
                type="button"
                className="delete-workspace-btn"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete Workspace
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirm-by-name delete modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete "{workspace.name}"?</h3>
            <p>
              This will permanently delete the workspace along with all its
              boards, lists, and cards. This action <strong>cannot be undone</strong>.
            </p>

            {/* Preview of the boards that will be deleted */}
            {boardsToDelete.length > 0 ? (
              <div className="delete-preview">
                <p className="delete-preview-label">
                  {boardsToDelete.length} board{boardsToDelete.length === 1 ? '' : 's'} will be permanently deleted:
                </p>
                <ul className="delete-preview-list">
                  {previewBoards.map(board => (
                    <li key={board._id || board.shortId} className="delete-preview-item">
                      <span
                        className="delete-preview-swatch"
                        style={{
                          background: board.cover?.sizes?.thumbnail || board.cover?.url
                            ? `url('${board.cover.sizes?.thumbnail || board.cover.url}') center/cover`
                            : board.cover?.coverColor?.[2] || 'var(--input-background-color)'
                        }}
                      />
                      <span className="delete-preview-title">{board.title}</span>
                    </li>
                  ))}
                  {remainingBoardCount > 0 && (
                    <li className="delete-preview-item delete-preview-more">
                      +{remainingBoardCount} more board{remainingBoardCount === 1 ? '' : 's'}
                    </li>
                  )}
                </ul>
              </div>
            ) : (
              <p className="delete-preview-empty">This workspace has no boards.</p>
            )}

            <p>
              Type <strong>{workspace.name}</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder={workspace.name}
              autoFocus
              disabled={isDeleting}
            />
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-delete-btn"
                disabled={deleteConfirm !== workspace.name || isDeleting}
                onClick={handleDeleteWorkspace}
              >
                {isDeleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSettings;

